import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtSpeed } from "@/lib/units";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plane, Plus, Pencil, Trash2, Fuel, Scale, Gauge } from "lucide-react";

export default function AircraftList() {
  const [items, setItems] = useState(null);
  const navigate = useNavigate();

  const load = async () => setItems((await api.get("/aircraft")).data);
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/aircraft/${id}`);
    toast.success("Avion supprimé");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-cyan-500/80">
            <Plane className="h-3.5 w-3.5" /> Flotte
          </div>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-50">Avions</h1>
          <p className="mt-1 text-sm text-slate-400">Fiches réutilisables avec performances et centrage.</p>
        </div>
        <Button data-testid="add-aircraft-button" onClick={() => navigate("/aircraft/new")}
          className="bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
          <Plus className="mr-1.5 h-4 w-4" /> Nouvel avion
        </Button>
      </div>

      {items === null ? (
        <div className="py-16 text-center text-slate-500">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="glass rounded-2xl py-16 text-center">
          <Plane className="mx-auto mb-3 h-10 w-10 text-slate-600" />
          <p className="text-slate-400">Aucun avion enregistré.</p>
          <Button onClick={() => navigate("/aircraft/new")} variant="link" className="mt-1 text-cyan-400">
            Créer votre premier avion
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((a) => (
            <div key={a.id} data-testid={`aircraft-card-${a.id}`}
              className="glass group rounded-2xl p-5 transition-all hover:border-cyan-500/50 hover:shadow-[0_0_20px_rgba(0,229,255,0.15)]">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-mono-data text-lg font-bold text-cyan-300">{a.registration || "—"}</div>
                  <div className="text-sm text-slate-400">{a.model || "Type inconnu"}</div>
                </div>
                <div className="flex gap-1">
                  <Link to={`/aircraft/${a.id}`} data-testid={`edit-aircraft-${a.id}`}
                    className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-cyan-300">
                    <Pencil className="h-4 w-4" />
                  </Link>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`delete-aircraft-${a.id}`}
                        className="rounded-lg p-2 text-slate-400 hover:bg-rose-500/10 hover:text-rose-300">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="border-slate-700 bg-[#0D1527] text-slate-100">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer cet avion ?</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                          {a.registration} sera définitivement supprimé.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="border-slate-700 bg-transparent text-slate-300">Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(a.id)} className="bg-rose-600 hover:bg-rose-500">Supprimer</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-800 pt-4 text-center">
                <div>
                  <Gauge className="mx-auto h-4 w-4 text-cyan-500/70" />
                  <div className="mt-1 font-mono-data text-xs text-slate-200">{a.tasCruise || 0} kt</div>
                  <div className="text-[10px] uppercase text-slate-500">TAS</div>
                </div>
                <div>
                  <Fuel className="mx-auto h-4 w-4 text-cyan-500/70" />
                  <div className="mt-1 font-mono-data text-xs text-slate-200">{a.fuelBurn || 0} L/h</div>
                  <div className="text-[10px] uppercase text-slate-500">Conso</div>
                </div>
                <div>
                  <Scale className="mx-auto h-4 w-4 text-cyan-500/70" />
                  <div className="mt-1 font-mono-data text-xs text-slate-200">{a.mtow || 0} kg</div>
                  <div className="text-[10px] uppercase text-slate-500">MTOW</div>
                </div>
              </div>
              <div className="mt-3 text-center text-[11px] text-slate-500">{fmtSpeed(a.tasCruise)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
